import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BusinessProfileForm } from "@/components/account/BusinessProfileForm";
import { SubscriptionManagement } from "@/components/account/SubscriptionManagement";
import { StripeAccount } from "@/components/account/StripeAccount";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AccountSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Check if there's a section to expand from localStorage
    const sectionToExpand = localStorage.getItem("expandAccordionSection");

    if (sectionToExpand) {
      // Force a complete component cycle before attempting to expand
      // This ensures Radix UI Accordion has fully initialized
      setTimeout(() => {
        setExpandedSection(sectionToExpand);
      }, 300);

      // Remove from localStorage to prevent unexpected expansions on future visits
      localStorage.removeItem("expandAccordionSection");
    }
  }, [location.key]); // Re-run when navigation occurs

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== "delete") {
      toast.error(t("toasts.type_delete"), {
        description: t("toasts.type_delete"),
      });
      return;
    }

    try {
      setIsDeleting(true);

      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");

      // Call our Edge Function to delete the user
      const response = await fetch(
        'https://mjatochkqafjodfnlbky.supabase.co/functions/v1/delete-user',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete account');
      }

      navigate("/");
      toast.success(t("toasts.account_deleted_success_title"), {
        description: t("toasts.account_deleted_success"),
      });
    } catch (error: any) {
      toast.error(t("toasts.account_deleted_failed_title"), {
        description: t("toasts.account_deleted_failed") + error.message,
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  // Force the accordion to render with the correct section expanded
  // by setting expandedSection as both a controlled value and defaultValue
  return (
    <div className="container mx-auto py-4 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <h1 className="font-archivoblack text-5xl font-bold text-[#34375B] mb-6 text-left">{t("account_settings.title")}</h1>

        {/* Key with expandedSection forces re-render when it changes */}
        <Accordion
          key={expandedSection || 'default-key'}
          type="single"
          collapsible
          className="w-full space-y-4"
          value={expandedSection}
          onValueChange={setExpandedSection}
          defaultValue={expandedSection}
        >
          <AccordionItem value="business-profile" className="border rounded-lg px-4 bg-[#8791F3] text-white">
            <AccordionTrigger className="py-4 hover:no-underline">
              <span className="text-xl font-semibold">{t("account_settings.business_profile", "Business Profile")}</span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-4">
              <BusinessProfileForm />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="subscription" className="border rounded-lg px-4 bg-[#8791F3] text-white">
            <AccordionTrigger className="py-4 hover:no-underline">
              <span className="text-xl font-semibold">{t("account_settings.subscription_and_usage", "Subscription & Usage")}</span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-4">
              <SubscriptionManagement />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="stripe-account" className="border rounded-lg px-4 bg-[#8791F3] text-white">
            <AccordionTrigger className="py-4 hover:no-underline">
              <span className="text-xl font-semibold">{t("account_settings.stripe_account", "Stripe Account")}</span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-4">
              <StripeAccount />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="danger-zone" className="border rounded-lg px-4 text-destructive/30 border-destructive/30 hover:border-destructive/80 hover:text-destructive/80">
            <AccordionTrigger className="py-4 hover:no-underline">
              <span className="text-xl font-bold">{t("account_settings.danger_zone", "Danger Zone")}</span>
            </AccordionTrigger>
            <AccordionContent className="pt-2 pb-4">
              <div className="space-y-4">
                <p className="text-muted-foreground font-bold text-sm">
                  {t("account_settings.delete_warning")}
                </p>
                <Button
                  variant="destructive"
                  className="bg-destructive/60 hover:bg-destructive hover:border-0 hover:text-desctructive hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  {t("account_settings.delete_account", "Delete Account")}
                </Button>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="max-w-[95vw] w-full sm:max-w-[425px] p-4 sm:p-6">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-bold text-[#34375B] text-lg sm:text-xl">{t("account_settings.are_you_sure", "Are you absolutely sure?")}</AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p className="font-thin text-xs sm:text-sm text-[#34375B]/80">
                  {t("account_settings.delete_paragraph")}
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li className="font-thin text-xs sm:text-sm text-[#34375B]/80">{t("account_settings.delete_list_1")}</li>
                  <li className="font-thin text-xs sm:text-sm text-[#34375B]/80">{t("account_settings.delete_list_2")}</li>
                  <li className="font-thin text-xs sm:text-sm text-[#34375B]/80">{t("account_settings.delete_list_3")}</li>
                  <li className="font-thin text-xs sm:text-sm text-[#34375B]/80">{t("account_settings.delete_list_4")}</li>
                </ul>
                <div className="mt-4">
                  <p className="mb-2 font-bold text-xs sm:text-sm text-[#34375B]/80">{t("account_settings.please_type", "Please type")} <span className="font-semibold">'delete'</span> {t("account_settings.to_confirm", "to confirm")}:</p>
                  <Input
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder={t("account_settings.type_delete", "Type 'delete' to confirm")}
                    className="max-w-full sm:max-w-[230px] font-thin text-xs sm:text-sm text-[#34375B]/80 border border-[#34375B]"
                  />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-4">
              <Button
                variant="outline"
                className="w-full sm:w-auto hover:bg-[#5CC2BB] hover:border-0 hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
                onClick={() => setShowDeleteDialog(false)}
              >
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                variant="destructive"
                className="w-full sm:w-auto hover:bg-destructive hover:border-0 hover:text-desctructive hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation.toLowerCase() !== "delete" || isDeleting}
              >
                {isDeleting ? t("account_settings.deleting", "Deleting...") : t("account_settings.delete_account", "Delete Account")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    </div>
  );
};

export default AccountSettings;
